/**
 * Constants related to Document Exchange tables.
 */

/**
 * Columns which align in the customer RFP table and the nested site RFP tables.
 * Widths are percentages values, but do not need to sum to 100%.
 * If they do not sum to 100%, all widths are scaled proportionally.
 */
export const CORRESPONDING_COLUMNS: {
  customerRFPColumnTitle: string
  siteRFPColumnTitle: string
  width: number
}[] = [
  {
    customerRFPColumnTitle: 'Project Name',
    siteRFPColumnTitle: 'Site Name',
    width: 30,
  },
  {
    customerRFPColumnTitle: 'Required Docs',
    siteRFPColumnTitle: 'Required Docs',
    width: 12,
  },
  {
    customerRFPColumnTitle: 'Reviews',
    siteRFPColumnTitle: 'Reviews',
    width: 8,
  },
  {
    customerRFPColumnTitle: 'Total System Size',
    siteRFPColumnTitle: 'System Size',
    width: 12,
  },
]

const ADDITIONAL_CUSTOMER_RFP_COLUMNS: ColumnTitleAndWidth[] = [
  { title: 'Pod', width: 8 },
  { title: 'Last Updated', width: 12 },
  { title: 'Updated By', width: 14 },
]

/**
 * Columns in the customer RFP table.
 * Widths are percentages values, but do not need to sum to 100%.
 * If they do not sum to 100%, all widths are scaled proportionally.
 */
export const CUSTOMER_RFP_COLUMNS: ColumnTitleAndWidth[] =
  CORRESPONDING_COLUMNS.map((column) => ({
    title: column.customerRFPColumnTitle,
    width: column.width,
  })).concat(ADDITIONAL_CUSTOMER_RFP_COLUMNS)

/**
 * Columns in the nested site RFP tables which do not align with columns in the customer RFP table.
 * Widths are percentages values, but do not need to sum to 100%.
 * If they do not sum to 100%, all widths are scaled proportionally.
 */
const ALL_ADDITIONAL_SITE_RFP_COLUMNS: ColumnTitleAndWidth[] = [
  { title: 'Phase', width: 12 },
  { title: 'NTP Estimate', width: 12 },
  { title: 'Address', width: 18 },
  { title: 'Channel Partner', width: 14 },
  { title: 'Last Updated', width: 12 },
  { title: 'Updated By', width: 14 },
]

/**
 * A subest of ALL_ADDITIONAL_SITE_RFP_COLUMNS that is shown to Client users.
 */
const CLIENT_ADDITIONAL_SITE_RFP_COLUMNS: ColumnTitleAndWidth[] = [
  { title: 'Phase', width: 12 },
  { title: 'Address', width: 18 },
  { title: 'Channel Partner', width: 14 },
  { title: 'Last Updated', width: 12 },
  { title: 'Updated By', width: 14 },
]

/**
 * Columns in the nested site RFP tables.
 * Widths are percentages values, but do not need to sum to 100%.
 * If they do not sum to 100%, all widths are scaled proportionally.
 */
export function getSiteRFPColumns(
  isClient: boolean = false
): ColumnTitleAndWidth[] {
  const additionalCols = isClient
    ? CLIENT_ADDITIONAL_SITE_RFP_COLUMNS
    : ALL_ADDITIONAL_SITE_RFP_COLUMNS
  return [
    ...CORRESPONDING_COLUMNS.map((column) => ({
      title: column.siteRFPColumnTitle,
      width: column.width,
    })),
    ...additionalCols,
  ]
}

/**
 * Columns in the document checklist tables.
 * The static widths are constant. Columns with
 * 'unset' width will scale equally to fill the remaining space.
 */
export const CHECKLIST_COLUMN_WIDTHS: string[] = [
  'unset', // name
  'unset', // upload status
  'unset', // spacer
  '65px', // review status
  '65px', // comment
  '120px', // updatedBy
  '120px', // updatedAt
  '40px', // kebab menu
]

/** The width of the customer RFP table element. */
export const CUSTOMER_RFP_TABLE_WIDTH = '100%'

/** The border width of the nested site RFP table element. */
export const SITE_RFP_TABLE_BORDER_WIDTH = '1px'

/** The padding of the nested site RFP table element. */
export const SITE_RFP_TABLE_PADDING = '1rem'

/** The width of the checklist table element. */
export const CHECKLIST_TABLE_WIDTH = '100%'

/** The maximum number of site RFPs before the nested table scrolls. */
export const MAX_SITE_RFPS_BEFORE_SCROLL = 5

/**
 * The left padding of the first cell in the nested site RFP table.
 * This offsets the negative left margin of the nested site RFP table.
 */
export const SITE_RFP_TABLE_FIRST_CELL_PADDING_LEFT = `calc(2 * ${SITE_RFP_TABLE_PADDING})`

/**
 * The width of the nested site RFP table element.
 * This is a CSS `calc` expression based on the scale factor,
 * the customer RFP table width,
 * the padding and border of the nested site RFP table element,
 * and the width of the scrollbar.
 */
export function getSiteRFPTableWidth(siteRFPCount: number, isClient: boolean) {
  const scrollbarWidth = siteRFPCount > MAX_SITE_RFPS_BEFORE_SCROLL ? 15 : 0
  return `calc(${getSiteRFPTableScaleFactor(
    isClient
  )} * (${CUSTOMER_RFP_TABLE_WIDTH} + (2 * ${SITE_RFP_TABLE_PADDING}) + calc(2 * ${SITE_RFP_TABLE_BORDER_WIDTH}) + ${scrollbarWidth}px))`
}

/**
 * Calculate the width of the nested site RFP table as a factor of the customer RFP table width.
 * The scale factor of the nested site RFP table.
 * This is a number greater than 1,
 * so the nested site RFP table is wider than the customer RFP table.
 */
function getSiteRFPTableScaleFactor(isClient: boolean = false) {
  const siteRFPColumnWidthSum = getSiteRFPColumns(isClient).reduce(
    (acc, column) => acc + column.width,
    0
  )
  const customerRFPColumnWidthSum = CUSTOMER_RFP_COLUMNS.reduce(
    (acc, column) => acc + column.width,
    0
  )
  return siteRFPColumnWidthSum / customerRFPColumnWidthSum
}

/**
 * The negative left margin of the nested site RFP table element.
 * Applying a negative margin to the left of the nested site RFP table
 * equal to its padding and border width lines up the columns in the nested site RFP table
 * with the columns in the customer RFP table.
 */
export const SITE_RFP_TABLE_MARGIN_LEFT = `calc(-${SITE_RFP_TABLE_PADDING} - ${SITE_RFP_TABLE_BORDER_WIDTH})`

/**
 * The title and width of a column in the customer RFP table or the nested site RFP table.
 */
interface ColumnTitleAndWidth {
  title: string
  width: number
}
